
import { motion } from 'framer-motion';

// Shared container style for consistency
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Seamless Collaboration (Real-time Edits)
export const RealTimeCollaborationVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col relative overflow-hidden">

                {/* Mock Slide Header */}
                <div className="mb-4 flex justify-between items-center relative z-10">
                    <div className="w-1/3 h-3 bg-gray-100 rounded animate-pulse" />
                    {/* Active Users Avatars */}
                    <div className="flex -space-x-2">
                        {['bg-blue-500', 'bg-purple-500', 'bg-amber-500'].map((c, i) => (
                            <div key={i} className={`w-6 h-6 rounded-full border-2 border-white ${c} flex items-center justify-center text-[8px] text-white font-bold`}>
                                {['A', 'S', 'J'][i]}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Mock Slide Content */}
                <div className="flex-1 rounded bg-gray-50 border border-gray-100 p-4 relative">

                    {/* Text Block being edited */}
                    <div className="mb-4">
                        <div className="text-gray-400 text-xs mb-1">Impact Summary</div>
                        <div className="h-2 w-3/4 bg-gray-200 rounded mb-2" />
                        <div className="h-2 w-1/2 bg-gray-200 rounded" />

                        {/* Cursor 1 (Alex) */}
                        <motion.div
                            className="absolute top-10 left-1/2"
                            animate={{ x: [0, 40, 0], y: [0, 10, 0] }}
                            transition={{ duration: 4, repeat: Infinity }}
                        >
                            <svg className="w-4 h-4 text-purple-500 transform -rotate-12 drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24"><path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19169L11.7118 9.20839L8.14441 9.20839C7.79542 9.20839 7.50002 9.49379 7.50002 9.84278V12.0007L5.65376 12.3673Z" /></svg>
                            <div className="bg-purple-500 text-white text-[8px] px-1 rounded-sm ml-3 -mt-1 shadow-sm">Alex</div>
                        </motion.div>
                    </div>

                    {/* Image Block being moved */}
                    <motion.div
                        className="w-24 h-16 bg-blue-100 rounded border border-blue-200 flex items-center justify-center relative"
                        animate={{ x: [0, 20, 0], y: [0, -10, 0] }}
                        transition={{ duration: 5, repeat: Infinity, delay: 1 }}
                    >
                        <svg className="w-6 h-6 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>

                        {/* Cursor 2 (Sam) */}
                        <motion.div
                            className="absolute -bottom-3 -right-3"
                        >
                            <svg className="w-4 h-4 text-blue-500 transform -rotate-12 drop-shadow-sm" fill="currentColor" viewBox="0 0 24 24"><path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19169L11.7118 9.20839L8.14441 9.20839C7.79542 9.20839 7.50002 9.49379 7.50002 9.84278V12.0007L5.65376 12.3673Z" /></svg>
                            <div className="bg-blue-500 text-white text-[8px] px-1 rounded-sm ml-3 -mt-1 shadow-sm">Sam</div>
                        </motion.div>
                    </motion.div>
                </div>

                {/* Comment Bubble Popups */}
                <motion.div
                    className="absolute bottom-8 right-8 bg-white border border-gray-100 shadow-lg rounded-lg p-2 max-w-[120px]"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: [0, 1, 1, 0], scale: [0.8, 1, 1, 0.8] }}
                    transition={{ duration: 4, repeat: Infinity, repeatDelay: 2 }}
                >
                    <div className="flex gap-2 mb-1">
                        <div className="w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center text-[6px] text-white">J</div>
                        <div className="text-[8px] font-bold text-gray-700">Jordan</div>
                    </div>
                    <div className="text-[8px] text-gray-500 leading-tight">Can we make this chart bigger?</div>
                </motion.div>

            </div>
        </VisualContainer>
    );
};

// Component 2: Role-Based Access (Permissions)
export const AccessControlVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-0 flex flex-col relative overflow-hidden">

                {/* Header */}
                <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-100 rounded-md flex items-center justify-center text-blue-600">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                        </div>
                        <div className="text-xs font-bold text-gray-700">Team Access</div>
                    </div>
                    <div className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded cursor-pointer hover:bg-blue-100">
                        + Invite Member
                    </div>
                </div>

                {/* User List */}
                <div className="flex-1 p-4 space-y-3">
                    {[
                        { name: "Sarah Connor", role: "Owner", color: "bg-emerald-100 text-emerald-700", status: "Admin" },
                        { name: "Mike Ross", role: "Editor", color: "bg-blue-100 text-blue-700", status: "Can Edit" },
                        { name: "Jessica P.", role: "Viewer", color: "bg-gray-100 text-gray-600", status: "View Only" }
                    ].map((user, i) => (
                        <motion.div
                            key={i}
                            className="flex items-center justify-between p-2 rounded-lg border border-transparent hover:border-gray-100 hover:bg-gray-50 transition-colors group"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: i * 0.2 }}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full ${user.color.split(' ')[0]} flex items-center justify-center text-[10px] font-bold`}>
                                    {user.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <div>
                                    <div className="text-[10px] font-bold text-gray-800">{user.name}</div>
                                    <div className="text-[8px] text-gray-400">{user.name.toLowerCase().replace(' ', '.')}@deckwale.com</div>
                                </div>
                            </div>

                            {/* Role Dropdown Simulator */}
                            <div className={`text-[9px] font-semibold px-2 py-1 rounded border ${user.role === 'Owner' ? 'bg-gray-100 border-gray-200 text-gray-500' : 'bg-white border-gray-200 text-gray-700 group-hover:border-blue-300 group-hover:text-blue-600'
                                } flex items-center gap-1 cursor-pointer`}>
                                {user.role}
                                {user.role !== 'Owner' && (
                                    <svg className="w-2 h-2 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Security Banner */}
                <div className="p-3 bg-blue-50/50 border-t border-blue-50 flex items-center gap-2 justify-center">
                    <svg className="w-3 h-3 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                    <div className="text-[9px] text-blue-600 font-medium">Enterprise-grade security enabled</div>
                </div>

            </div>
        </VisualContainer>
    );
};
